import { useEffect, useRef, useState, type ReactNode } from "react";

interface FlipSectionProps {
  children: ReactNode;
  className?: string;
}

export function FlipSection({ children, className = "" }: FlipSectionProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const update = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // progress: -1 (below viewport) → 0 (centered) → 1 (above viewport)
      const center = rect.top + rect.height / 2;
      const raw = (vh / 2 - center) / vh;
      const clamped = Math.max(-1, Math.min(1, raw));
      setProgress(clamped);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  // Rotate on X axis: -90deg (coming from below) → 0 (centered) → 90 (going up)
  const rotateX = -progress * 90;
  // Fade out at the edges
  const opacity = 1 - Math.min(1, Math.abs(progress) * 1.2);
  // Slight translate for depth feel
  const translateY = progress * -40;

  return (
    <section
      ref={ref}
      className={`flex min-h-screen items-center justify-center px-6 ${className}`}
      style={{ perspective: "1200px" }}
    >
      <div
        className="max-w-3xl text-center will-change-transform"
        style={{
          transform: `translateY(${translateY}px) rotateX(${rotateX}deg)`,
          opacity,
          transformStyle: "preserve-3d",
          transformOrigin: "center center",
          transition: "transform 0.1s linear, opacity 0.1s linear",
        }}
      >
        {children}
      </div>
    </section>
  );
}

export default FlipSection;